import { derive } from './derive'
import { proxy, readonly, subscribe as subscribeToProxy } from '../reactivity/proxy'
import type { AlegraCompany, AlegraUser, SessionData, SessionPublic } from '../types'
import type { UserSelfService } from './UserSelfService'
import type { StoreOp } from '@alegradev/smile-core/store'

function setLocalStorage(key: string, value: string | undefined | null): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
  if (value == null) return
  try {
    localStorage.setItem(key, value)
  } catch {
    // quota / privacy mode — ignore
  }
}

function emptySession(): SessionData {
  // Runtime starts empty; consumers gate on `loaded` if they need a hard
  // guarantee. The `as` casts let downstream code treat populated fields as
  // required without `??` defaults — see the JSDoc on AlegraUser/AlegraCompany
  // in `types.ts` for the rationale.
  const user = {} as AlegraUser
  const company = {} as AlegraCompany
  return { user, company, loaded: false, ...derive(user, company) }
}

/**
 * Framework-agnostic session store.
 *
 * Built on the reactive-proxy engine (`src/reactivity/proxy.ts`) — `state`
 * is a single, long-lived deeply-reactive object; `apply()` mutates it in
 * place field-by-field rather than replacing it wholesale, so subscribers
 * only get notified for fields that actually changed (the engine no-ops a
 * `set` when the value is reference-identical to the previous one).
 *
 * Microfronts read this through `window._alegraApp.stores.session` (the
 * public view returned by `publicView()`) — a readonly view of the same
 * live object, bindable into any framework's own reactivity via its
 * `target` + `subscribe`.
 */
export class SessionStore {
  private readonly state: SessionData = proxy(emptySession())
  private readonly userSelfService: UserSelfService

  constructor(userSelfService: UserSelfService) {
    this.userSelfService = userSelfService
  }

  /** The live, writable state — for the store's owner only. Consumers get `publicView().target` (readonly) instead. */
  get(): SessionData {
    return this.state
  }

  /**
   * Subscribe to granular changes — the callback receives exactly which
   * paths changed (see `StoreOp`), not a wholesale replacement snapshot.
   * Returns an unsubscribe function.
   */
  subscribe(fn: (ops: StoreOp[]) => void, notifyInSync?: boolean): () => void {
    return subscribeToProxy(this.state, fn, notifyInSync)
  }

  /**
   * Apply new user/company data, recompute derived fields, persist identity
   * hints to localStorage. Mutates the existing state object field-by-field
   * — granular, not a wholesale replacement — so subscribers only see ops
   * for fields whose value actually changed.
   */
  apply(user: AlegraUser, company: AlegraCompany): void {
    Object.assign(this.state, { user, company, loaded: true, ...derive(user, company) })
    setLocalStorage('al-user-language', user.language)
    setLocalStorage('al-company-appVersion', company.applicationVersion)
  }

  /**
   * Apply a raw `/v1/users/self` payload directly. Splits `company` off the
   * top-level object and delegates to `apply()` — the wire shape nests
   * company under `data.company`, but the store treats user and company as
   * peer fields. A missing/null `company` is treated as `{}`.
   */
  applyRawPayload(raw: Record<string, unknown>): void {
    const rawPayload = { ...raw }
    const company = (rawPayload.company ?? {}) as AlegraCompany
    delete rawPayload.company
    this.apply(rawPayload as unknown as AlegraUser, company)
  }

  /**
   * Re-fetch `/v1/users/self` through `UserSelfService` and update the store.
   * Throws if the fetch fails or returns no data.
   */
  async reloadUserInfo(): Promise<void> {
    const data = await this.userSelfService.load()
    if (!data) {
      throw new Error('[alegra-commons-runtime] reloadUserInfo: no user data returned')
    }
    this.applyRawPayload(data)
  }

  /**
   * One-shot: resolves with the current state once `loaded` is true.
   * Returns immediately if the session has already loaded; otherwise
   * subscribes for the next change and unsubscribes as soon as it resolves.
   */
  whenLoaded(): Promise<SessionData> {
    if (this.state.loaded) return Promise.resolve(this.state)
    return new Promise((resolve) => {
      const unsubscribe = this.subscribe(() => {
        if (!this.state.loaded) return
        unsubscribe()
        resolve(this.state)
      }, true)
    })
  }

  /**
   * Readonly handle for exposure on `window._alegraApp.stores.session`.
   * `target` is a live, deeply readonly view of the same state — reads
   * always reflect the current value, but writing through it throws.
   * Mutation stays exclusive to this class's `apply()`.
   */
  publicView(): SessionPublic {
    return {
      target: readonly(this.state),
      subscribe: (fn, notifyInSync) => this.subscribe(fn, notifyInSync),
      reloadUserInfo: () => this.reloadUserInfo(),
      whenLoaded: () => this.whenLoaded(),
    }
  }
}
